import { AccountLoadError } from '@components/accountLoadError';
import { Avatar } from '@components/avatar';
import { LoaderSpinner } from '@components/loader/spinner';
import { Drawer } from '@components/ui/drawer/component';
import { Separator } from '@components/ui/separator';
import { usePanel } from '@hooks/usePanel';
import { OfficeTab } from '@root/globalTypes';
import { cn } from '@utils/cn';
import { parseRoute } from '@utils/i18n';
import { kebabToCamelCase } from '@utils/textHelpers';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router';
import { useAccountActions } from '../accountActions';
import { icons } from '../constants';
import { MobileMenuProps } from './types';

export const MobileMenu = ({
  open,
  setOpen,
  routes,
  tabError,
  fullName,
  showAccount,
  envLabel,
  accountError,
}: MobileMenuProps) => {
  const { t } = useTranslation('app', { keyPrefix: 'navigation' });
  const { pathname } = useLocation();
  const { parseUrl } = usePanel();
  const close = () => setOpen(false);
  const accountActions = useAccountActions({ onAction: close, fullWidth: true });

  return (
    <Drawer
      open={open}
      setOpen={setOpen}
      className="max-w-[300px]"
      title={
        showAccount ? (
          <span className="flex items-center gap-2">
            <Avatar name={fullName} hideTooltip />
            <span className="truncate">{fullName}</span>
          </span>
        ) : (
          t('menu')
        )
      }>
      <div className="flex flex-col overflow-y-auto py-2">
        {!routes.length &&
          (accountError ? (
            <AccountLoadError {...accountError} />
          ) : (
            <div className="flex justify-center p-4">
              <LoaderSpinner />
            </div>
          ))}
        <ul className="flex flex-col">
          {routes.map((route) => {
            const tab = kebabToCamelCase(route) as OfficeTab;
            return (
              <li key={route}>
                <NavLink
                  onClick={(e) => {
                    close();
                    if (pathname !== `/${parseRoute(route)}`) return;
                    e.preventDefault();
                  }}
                  to={`/${parseRoute(parseUrl(route))}`}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2 border-l-[3px] border-transparent px-2 font-semibold text-neutral-600 hover:text-primary-500 [&>svg]:h-9 [&>svg]:w-9 [&>svg]:shrink-0',
                      isActive && 'border-primary-500 bg-primary-50 text-primary-500',
                    )
                  }>
                  {icons[tab]}
                  <span className="grow">{t(tab)}</span>
                  {tabError(tab) && (
                    <span className="h-3 w-3 shrink-0 rounded-full bg-danger-400" />
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
        {showAccount && (
          <>
            <Separator className="my-2" />
            <div className="flex flex-col px-2">{accountActions}</div>
          </>
        )}
        {envLabel && <p className="px-4 pt-2 text-xs text-neutral-400">{envLabel}</p>}
      </div>
    </Drawer>
  );
};
